import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Loader2, Receipt, Search, X } from 'lucide-react';
import { apiBaseUrl } from '../../services/api';

// Petits éléments communs de la page Dépenses : montants, dates, pastilles, choix d'un projet.

const CODES = { '€': 'EUR', $: 'USD', '': 'EUR' };
export const currencyCode = (c) => CODES[c ?? ''] || (c && c.length === 3 ? c : 'EUR');

export const money = (value, currency = '€') => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: currencyCode(currency) }).format(Number(value));
};

export const qty = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  return Number.isInteger(n) ? String(n) : n.toLocaleString('fr-FR', { maximumFractionDigits: 2 });
};

export const dayLabel = (iso) => (iso
  ? new Date(`${String(iso).slice(0, 10)}T00:00:00`).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })
  : '');

export const fullDate = (iso) => (iso
  ? new Date(`${String(iso).slice(0, 10)}T00:00:00`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
  : '');

export const monthLabel = (iso) => {
  if (!iso) return 'Sans date';
  const label = new Date(`${String(iso).slice(0, 7)}-01T00:00:00`).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  return label.charAt(0).toUpperCase() + label.slice(1);
};

export const fileHref = (url) => (url ? `${apiBaseUrl}${url}` : null);

// Reçus du même paiement gardés avec la facture (mail Stripe : facture + reçu = une seule dépense) : justificatifs de
// paiement, ni lus ni envoyés à Pennylane.
export const ReceiptLinks = ({ receipts }) => (receipts || []).map((receipt) => (
  <a key={receipt.url} href={fileHref(receipt.url)} target="_blank" rel="noopener noreferrer"
    title={`Reçu de paiement : ${receipt.name}`}
    className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-xs text-gray-500 hover:bg-gray-100 hover:text-gray-700">
    <Receipt className="h-4 w-4" /> Reçu
  </a>
));

const TONES = {
  gray: 'bg-gray-100 text-gray-700 ring-gray-200',
  blue: 'bg-blue-50 text-blue-700 ring-blue-200',
  green: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  amber: 'bg-amber-50 text-amber-800 ring-amber-200',
  red: 'bg-red-50 text-red-700 ring-red-200',
  violet: 'bg-violet-50 text-violet-700 ring-violet-200',
};

export const Pill = ({ tone = 'gray', icon: Icon, children, title, className = '' }) => (
  <span title={title} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${TONES[tone]} ${className}`}>
    {Icon && <Icon className="h-3 w-3" />}
    {children}
  </span>
);

export const Spinner = ({ className = 'h-4 w-4' }) => <Loader2 className={`${className} animate-spin text-gray-400`} />;

// Couleur stable par projet (même projet, même teinte partout sur la page).
const PALETTE = ['bg-sky-500', 'bg-violet-500', 'bg-emerald-500', 'bg-amber-500', 'bg-rose-500', 'bg-teal-500',
  'bg-indigo-500', 'bg-lime-600', 'bg-orange-500', 'bg-fuchsia-500'];
export const projectDot = (projectId) => PALETTE[Math.abs(Number(projectId) || 0) % PALETTE.length];

export const SearchBox = ({ value, onChange, placeholder, className = '' }) => (
  <label className={`relative flex items-center ${className}`}>
    <Search className="pointer-events-none absolute left-2.5 h-4 w-4 text-gray-400" />
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full rounded-lg border border-gray-200 bg-white py-1.5 pl-8 pr-7 text-sm shadow-sm placeholder:text-gray-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
    />
    {value && (
      <button type="button" onClick={() => onChange('')} className="absolute right-2 text-gray-400 hover:text-gray-600" aria-label="Effacer">
        <X className="h-3.5 w-3.5" />
      </button>
    )}
  </label>
);

const STATUS_ORDER = { in_progress: 0, proposition: 1, completed: 2 };

// Choix d'un projet : liste filtrée au clavier (projets en cours d'abord).
export const ProjectSelect = ({ projects, value, onChange, placeholder = 'Projet…', autoFocus = false, className = '' }) => {
  const [open, setOpen] = useState(autoFocus);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const ref = useRef(null);
  const current = projects.find((p) => p.id === value);
  const filtered = useMemo(() => {
    const words = query.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').split(/\s+/).filter(Boolean);
    return [...projects]
      .filter((p) => {
        const text = `${p.name} ${p.project_number || ''} ${p.client_name || ''}`.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
        return words.every((w) => text.includes(w));
      })
      .sort((a, b) => (STATUS_ORDER[a.status] ?? 3) - (STATUS_ORDER[b.status] ?? 3) || a.name.localeCompare(b.name))
      .slice(0, 40);
  }, [projects, query]);

  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => { if (ref.current && !ref.current.contains(event.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const choose = (project) => {
    onChange(project.id);
    setOpen(false);
    setQuery('');
  };

  return (
    <div ref={ref} className={`relative ${className}`}>
      {open ? (
        <input
          autoFocus
          value={query}
          onChange={(e) => { setQuery(e.target.value); setActive(0); }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(i + 1, filtered.length - 1)); }
            if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
            if (e.key === 'Enter' && filtered[active]) { e.preventDefault(); choose(filtered[active]); }
            if (e.key === 'Escape') setOpen(false);
          }}
          placeholder="Chercher un projet…"
          className="w-full rounded-md border border-blue-300 bg-white px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
        />
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex w-full items-center justify-between gap-2 rounded-md border border-gray-200 bg-white px-2 py-1 text-left text-sm hover:border-gray-300"
        >
          <span className={`truncate ${current ? 'text-gray-800' : 'text-gray-400'}`}>
            {current ? current.name : placeholder}
          </span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-gray-400" />
        </button>
      )}
      {open && (
        <ul className="absolute z-30 mt-1 max-h-64 w-72 overflow-auto rounded-lg border border-gray-200 bg-white py-1 text-sm shadow-lg">
          {filtered.length === 0 && <li className="px-3 py-2 text-gray-400">Aucun projet</li>}
          {filtered.map((p, index) => (
            <li key={p.id}>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); choose(p); }}
                onMouseEnter={() => setActive(index)}
                className={`flex w-full items-center gap-2 px-3 py-1.5 text-left ${index === active ? 'bg-blue-50' : ''}`}
              >
                <span className={`h-2 w-2 shrink-0 rounded-full ${projectDot(p.id)}`} />
                <span className="truncate text-gray-800">{p.name}</span>
                {p.project_number && <span className="shrink-0 text-xs text-gray-400">{p.project_number}</span>}
                {p.status !== 'in_progress' && (
                  <span className="ml-auto shrink-0 text-[10px] uppercase tracking-wide text-gray-400">
                    {p.status === 'completed' ? 'terminé' : p.status === 'proposition' ? 'proposition' : p.status}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
